<script setup lang="ts">
import { defineProps, computed } from 'vue';
import useStickyAlerts from '@/modules/alerts/composables/useStickyAlerts';
import { NotificationStickyAlertPropsSchema } from '@/modules/alerts/models/notification-sticky-alert-props';
import { STICKY_ALERT_TYPE } from '@/modules/alerts/models/sticky-alert-type';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { findAlert, answerAlert, setupTimeout } = useStickyAlerts();

const props = defineProps<NotificationStickyAlertPropsSchema>();

const alert = findAlert(STICKY_ALERT_TYPE.NOTIFICATION, props.id);

const messageSegments = computed(() => {
  const subMessage = props.options?.body;
  const orderId = props.options?.orderId;
  const hasLink = !!props.options?.link;

  if (!hasLink) {
    return [{ text: subMessage, applyLinkStyle: false }];
  }

  if (orderId && subMessage.includes(orderId)) {
    const parts = subMessage.split(orderId);
    const segments: Array<{ text: string; applyLinkStyle: boolean }> = [];

    parts.forEach((part, index) => {
      if (part) segments.push({ text: part, applyLinkStyle: false });
      if (index < parts.length - 1) segments.push({ text: orderId, applyLinkStyle: true });
    });

    return segments;
  }

  return [{ text: subMessage, applyLinkStyle: true }];
});

const close = () => {
  answerAlert(alert, { result: 'DISMISS' });
};

const handleClick = () => {
  const link = props?.options?.link;

  if (link) {
    close();
    window.location.href = link;
  }
};

setupTimeout(STICKY_ALERT_TYPE.NOTIFICATION, props.id, alert, props.options?.timeoutMs);
</script>

<template>
  <div class="accented-container">
    <div class="accent" data-testid="notification-alert-accent"></div>
    <div class="basic-alert" :class="{ clickable: props.options?.link }" @click="handleClick">
      <div class="alert-header d-flex justify-content-start align-items-center">
        <div class="pr-3">
          <font-awesome-icon
            :icon="['fas', 'bell-ring']"
            size="lg"
            data-testid="notification-alert-bell-icon"
          />
        </div>
        <div class="flex-grow-1 font-weight-bold">
          {{ props.title }}
        </div>
        <div
          class="cursor-pointer pr-1"
          @click.stop="close"
          data-testid="notification-alert-close-button"
        >
          <font-awesome-icon icon="xmark" size="lg" class="close-icon" />
        </div>
      </div>
      <div class="separator"></div>
      <div class="alert-content">
        <span data-testid="notification-alert-message">
          <template v-for="(segment, index) in messageSegments" :key="index">
            <span :class="{ 'link-text': segment.applyLinkStyle }">
              {{ segment.text }}
            </span>
          </template>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '~@/assets/styles/_tools.scss';
@import '~@/assets/styles/_settings.scss';

$border-radius: 0.5rem;

.accented-container {
  position: relative;
  width: 100%;
}

.accent {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: $border-radius;
  border-top-left-radius: $border-radius;
  border-bottom-left-radius: $border-radius;
  background-color: gray;
}

.basic-alert {
  width: 100%;
  background-color: $body-bg;
  border-radius: $border-radius;
  margin-left: 0.25rem;
  border-style: solid;
  border-width: rem(1px);
  border-color: color-contrast($body-bg);
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.25);

  &.clickable {
    cursor: pointer;
  }
}

.alert-header {
  padding: 0.75rem 0.5rem 0.75rem 0.75rem;
}

.separator {
  height: rem(1px);
  background-color: color-contrast($body-bg);
  opacity: 0.1;
}

.alert-content {
  padding: 0.75rem;
}

.link-text {
  color: $link-color;
  text-decoration: underline;
}

.close-icon {
  transform: scale(1.3);
}
</style>
